import { GAPI_API_KEY, GAPI_DISCOVERY_DOCS, ZANICHELLI_IDP_PINGINTERVAL } from '../config';
import { ERROR } from '../constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

import { heartbeat } from '../actions/heartbeat';
import { useLocation } from 'react-router';
import useScript from 'react-script-hook';

const getSize = () => {
    return {
        innerHeight: window.innerHeight,
        innerWidth: window.innerWidth,
        outerHeight: window.outerHeight,
        outerWidth: window.outerWidth,
    };
};

export const useWindowSize = () => {
    let [windowSize, setWindowSize] = useState(getSize());

    const handleResize = () => {
        setWindowSize(getSize());
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return windowSize;
};

const getScrollPosition = () => {
    return { x: window.pageXOffset, y: window.pageYOffset };
};

export const useScrollPosition = () => {
    const [position, setScrollPosition] = useState(getScrollPosition());

    const handleScroll = () => {
        setScrollPosition(getScrollPosition());
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return position;
};

export const useInterval = (callback, delay, step = delay / 10) => {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        const start = new Date().getTime();
        let delayCount = 0;

        function tick() {
            const totalTime = new Date().getTime() - start;
            let i = Math.floor(totalTime / delay) - delayCount;

            while (i--) {
                delayCount++;
                savedCallback.current();
            }
        }

        if (delay !== null) {
            let id = setInterval(tick, step);
            return () => clearInterval(id);
        }
    }, [delay, step]);
};

export const useOnlineStatus = () => {
    const [onlineStatus, setOnlineStatus] = useState(navigator.onLine);

    const updateOnlineStatus = () => {
        setOnlineStatus(navigator.onLine);
    };

    useEffect(() => {
        window.addEventListener('offline', updateOnlineStatus);

        window.addEventListener('online', updateOnlineStatus);

        return () => {
            window.removeEventListener('online', updateOnlineStatus);
            window.removeEventListener('offline', updateOnlineStatus);
        };
    }, []);

    return onlineStatus;
};

export function useOnClickOutside(ref, handler) {
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('click', listener, true);
        return () => {
            document.removeEventListener('click', listener, true);
        };
    }, [ref, handler]);
}

export function useMouseDownOutside(ref, handler) {
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }

            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}

export function useKeyDown(targetKey) {
    const [keyPressed, setKeyPressed] = useState(false);

    useEffect(() => {
        const downHandler = ({ key }) => {
            key === targetKey ? setKeyPressed(true) : setKeyPressed(false);
        };

        window.addEventListener('keydown', downHandler);
        return () => {
            window.removeEventListener('keydown', downHandler);
        };
    }, [targetKey]);

    return keyPressed;
}

export function useMathJaxStatus() {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const listenMathJaxHubQueue = () => {
            window.MathJax.startup = {
                ready() {
                    window.MathJax.startup.defaultReady();
                    setIsLoading(true);
                    window.MathJax.startup.promise.then(() => {
                        setIsLoading(false);
                    });
                },
            };
        };

        listenMathJaxHubQueue();
    }, []);

    return isLoading;
}

export function useGapi() {
    const [loadingGsi, errorGsi] = useScript({ src: 'https://accounts.google.com/gsi/client' });
    const [loadingClient, errorClient] = useScript({ src: 'https://apis.google.com/js/api.js' });

    const errored = errorClient || errorGsi;
    const [gapiStatus, setGapiStatus] = useState({ success: false, pending: true, errored: errored });

    const gsiLoaded = useRef(false);
    const gsiPending = useRef(true);
    const clientLoaded = useRef(false);
    const clientPending = useRef(true);

    useEffect(() => {
        if (!loadingClient) {
            window.gapi.load('client', async () => {
                try {
                    await window.gapi.client.init({
                        apiKey: GAPI_API_KEY,
                        discoveryDocs: GAPI_DISCOVERY_DOCS,
                    });
                    clientLoaded.current = true;
                    clientPending.current = false;
                    setGapiStatus({
                        success: gsiLoaded.current,
                        pending: gsiPending.current,
                        errored: false,
                    });
                } catch {
                    setGapiStatus({ success: false, pending: false, errored: true });
                }
            });
        }
    }, [loadingClient]);

    useEffect(() => {
        if (!loadingGsi) {
            gsiLoaded.current = true;
            gsiPending.current = false;
            setGapiStatus({
                success: clientLoaded.current,
                pending: clientPending.current,
                errored: false,
            });
        }
    }, [loadingGsi]);

    return [gapiStatus];
}
export function useGoogleCourseInfo(courseId, userInfo) {
    const [gCourseInfo, setGCourseInfo] = useState(null);
    const [gCourseInfoStatus, setGCourseInfoStatus] = useState({
        success: false,
        pending: true,
        errored: false,
    });

    useEffect(() => {
        const initGoogleCourseInfo = async () => {
            if (!userInfo) {
                setGCourseInfoStatus({
                    success: false,
                    pending: false,
                    errored: true,
                });
                return;
            }

            if (courseId && userInfo.access_token) {
                let teachersList = [];
                let studentsList = [];
                try {
                    const {
                        result: { teachers },
                    } = await window.gapi.client.classroom.courses.teachers.list({
                        courseId: courseId,
                    });
                    teachersList = teachers;
                } catch {
                    teachersList = [];
                }
                try {
                    const {
                        result: { students },
                    } = await window.gapi.client.classroom.courses.students.list({
                        courseId: courseId,
                    });
                    studentsList = students;
                } catch {
                    studentsList = [];
                }
                setGCourseInfo({ teachers: teachersList, students: studentsList });
                setGCourseInfoStatus({
                    success: true,
                    pending: false,
                    errored: false,
                });
            }
        };
        initGoogleCourseInfo().catch(() => {
            setGCourseInfoStatus({
                success: false,
                pending: false,
                errored: true,
            });
            setGCourseInfo(null);
        });
    }, [courseId, userInfo]);
    return [gCourseInfoStatus, gCourseInfo];
}

export function useHeartbeat() {
    const error = useSelector(
        (state) =>
            state.homework?.status?.error ||
            state.status?.httpStatu?.error ||
            state.auth?.status?.error ||
            state.assignment?.httpStatus?.error,
    );
    const notLoggedModalShowed = useSelector((state) => state.modal?.notLoggedModalShowed);
    const loginId = useSelector((state) => state.auth?.loginId);
    const isApplicationEmbedded = useSelector((state) => state.application.isEmbedded);
    const isPreview = useSelector((state) => state.application.isPreview);
    const isTryme = useSelector((state) => state.application.isTryme);
    const googleCourseWorkId = useSelector((state) => state.application.googleCourseWorkId);
    const dispatch = useDispatch();
    const location = useLocation();
    const hasDocumentFocus = useDocumentFocus();
    const [wasDocumentFocused, setDocumentWasFocused] = useState(hasDocumentFocus);
    const [heartbeatInterval, setHeartbeatInterval] = useState(0);

    const { pathname } = location;

    const checkHeartbeat = useMemo(() => {
        // Se non abilitato FT non controllo
        if (!ZANICHELLI_IDP_PINGINTERVAL) return false;

        // Se in google classroom non fare il check
        if (googleCourseWorkId) return false;

        // Non controllare se in pagina di errore
        if (error || pathname === ERROR) return false;

        // Applicazione caricata nell'iframe come per ZTE BO
        if (isApplicationEmbedded) return false;

        // Login ID non esiste
        if (!loginId) return false;

        // Modale già mostrata
        if (notLoggedModalShowed) return false;

        // ZTE BO Anteprima
        if (isPreview) return false;

        // Tryme tutor
        if (isTryme) return false;

        return true;
    }, [googleCourseWorkId, error, isApplicationEmbedded, isPreview, isTryme, loginId, notLoggedModalShowed, pathname]);

    useEffect(() => {
        // Il documento passa da NO "focus" -> "focus"
        if (!wasDocumentFocused && hasDocumentFocus && checkHeartbeat) {
            dispatch(heartbeat());
        }

        // Salviamo lo stato "focus" del documento
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDocumentWasFocused(hasDocumentFocus);

        // Non controlliamo il battito
        if (!checkHeartbeat) {
            heartbeatInterval > 0 && clearInterval(heartbeatInterval);
            setHeartbeatInterval(0);
            return;
        }

        // Controlliamo il battito e instanziamo il successivo controllo periodico
        if (heartbeatInterval === 0) {
            dispatch(heartbeat());
            const interval = setInterval(() => dispatch(heartbeat()), ZANICHELLI_IDP_PINGINTERVAL);
            setHeartbeatInterval(interval);
        }
    }, [checkHeartbeat, dispatch, hasDocumentFocus, heartbeatInterval, wasDocumentFocused, setDocumentWasFocused]);
}

const hasFocus = () => typeof document !== 'undefined' && document.hasFocus();

export const useDocumentFocus = () => {
    const [focused, setFocused] = useState(hasFocus);

    useEffect(() => {
        const onFocus = () => setFocused(true);
        const onBlur = () => setFocused(false);

        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    return focused;
};
